import { useState, useEffect, useRef, memo, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import {
  Flex,
  Box,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Button,
  Icon,
} from '@chakra-ui/react';
import {
  SelectOptionWithIcon,
  SelectWithDescriptionOption,
} from '@/components/formBuilder/SelectComponents';
import moment from 'moment';
import { debounce } from 'lodash-es';
import { FormBuilder } from '@/components/formBuilder';
import { Gmeet, Teams, Zoom } from '@/assets/icons/location';
import { timeIntervals, durationIntervals, rangeIntervals } from './time';
import { getAtsJobCreate } from '@/store/atsJobCreate';
import {
  selectInterviewDetails,
  selectCreatedInterviewDetails,
  selectInterviewersList,
  selectSchedulingLoader,
  selectWeekRange,
  selectEvents,
  selectInterviewerId,
  selectTimezone,
  selectDuration,
} from '@/store/atsScheduling/selectors';

import { getAtsJobScorecardParams } from '@/store/atsJobDetails';

import DatePickerField from './DatePickerField';
import {
  Email,
  ScoreCard,
} from '../../ats-job-creation/tabs/interview-plan/stage/activity/activity-sections/schedule-interview';
import {
  useCreateInterviewMutation,
  useUpdateInterviewMutation,
  useScheduleInterviewMutation,
  useUpdateScheduleInterviewMutation,
  useFetchSuggestedInterviewerDetailsMutation,
  useFetchScheduleSlotsMutation,
  useFetchScheduleSlotsDataMutation,
  useFetchLocationQuery,
  useFetchZoomLimitMutation,
} from '@/store/query/scheduling';
import {
  addMinuteWithDuration,
  combineTimeAndDate,
  configureCreateInterviewData,
  getNext15MinuteMark,
  isGreater,
  isSameDay,
  isToday,
} from '../utils';
import AddInterviewersField from './AddInterviewsField';
import { updateDuration } from '@/store/atsScheduling';
import { useRetry } from '@/hooks/use-retry';

import { useShowToast } from '@/hooks';
import { fetchSuggestedInterviewerId } from '@/store/atsScheduling/actions';
import { resetInterviewerId } from '@/store/atsScheduling';
import { Building07 } from '@kula-ai/icons/outline/Building07';
import { Phone } from '@kula-ai/icons/outline/Phone';
import { XClose } from '@kula-ai/icons/outline/XClose';
import { ERRORS } from './error';
import { usePermission } from '@/ui/permission';

import TimeField from './TimeField';
import NoteTakerField from './NoteTakerField';
import SelectOptionWithAvailability from './SelectOptionWithAvailability';
import { useKFlags } from '@/hooks/use-k-flags';
import { CustomTabWithStep } from '@/components/ats/Tabs';
import { KText } from '@/ui-library/components/text';

function SchedulingForm({
  event,
  onEventChange = () => {},
  selectedInterviewers = () => {},
  handleFetchedScheduleSlots = () => {},
  actionId,
  jobId,
  stageId,
  candidateId,
  applicationId,
  interviewId,
  scheduleId,
  variant = 'manual',
  onSuccess = () => {},
  onClose = () => {},
  userInfo,
  changedDateRange,
  changeDateRange,
  updateInterviewName,
}) {
  const [activeStart, setActiveStart] = useState(null);
  const [activeStartIndex, setActiveStartIndex] = useState(null);
  const [activeEnd, setActiveEnd] = useState(null);
  const [activeDate, setActiveDate] = useState(null);
  const [activeEndDate, setActiveEndDate] = useState(null);
  const [enableEndDate, setEnableEndDate] = useState(false);
  const [isClosed, setClosed] = useState(true);
  const [loadTabsContent, setLoadTabsContent] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [dateRange, setDateRange] = useState('');
  const [isCustomType, setIsCustomType] = useState(false);
  const [interviewType, setInterviewType] = useState('');
  const [isScheduledTypeModified, setScheduledTypeModified] = useState(false);
  const [IsNoSlotsAvailable, setIsNoSlotsAvailable] = useState(false);
  // const [emailError, setEmailError] = useState(false);
  // const [formError, setFormError] = useState(false);
  const formRef = useRef(null);
  const { feDisableAiNotetaker } = useKFlags();
  const emailRef = useRef(null);
  const scoreCardRef = useRef(null);
  const interviewers = useSelector(selectInterviewersList);
  const interviewDetails = useSelector(selectInterviewDetails);
  const createdInterviewDetails = useSelector(selectCreatedInterviewDetails);
  const selectedInterviewerId = useSelector(selectInterviewerId);
  const weekRange = useSelector(selectWeekRange);
  const events = useSelector(selectEvents);
  const stateDuration = useSelector(selectDuration);
  const [createInterview, { isLoading: isCreateInterviewLoading }] =
    useCreateInterviewMutation();
  const [updateInterview, { isLoading: isUpdateInterviewLoading }] =
    useUpdateInterviewMutation();
  const [scheduleInterview, { isLoading: isSchedulingInterviewLoading }] =
    useScheduleInterviewMutation();
  const [
    updateScheduleInterview,
    { isLoading: isUpdateSchedulingInterviewLoading },
  ] = useUpdateScheduleInterviewMutation();
  const selectedTimezone = useSelector(selectTimezone);
  const [fetchSuggestedInterviewerDetails] =
    useFetchSuggestedInterviewerDetailsMutation();
  const [fetchScheduleSlots, { data: scheduleSlotsData }] =
    useFetchScheduleSlotsMutation();
  const [fetchScheduleSlotsData] = useFetchScheduleSlotsDataMutation();
  const { data: locations } = useFetchLocationQuery();
  const [fetchZoomLimit] = useFetchZoomLimitMutation();

  const dispatch = useDispatch();
  const toast = useShowToast();

  const { getOffices } = useSelector(getAtsJobCreate);
  const defaultScorecardParams = useSelector(getAtsJobScorecardParams);

  const { data: offices = [], isLoading: isOfficeLoading } = getOffices;

  const { tRangeOfDays } = useKFlags();

  const enableNoteTaker = usePermission({
    permission: 'api_ats_v1_note_taker.update_setting?',
  });

  const isLoading = useSelector(selectSchedulingLoader);
  const isAdhoc = !actionId;
  const isReschedule = interviewId || scheduleId;

  const isFormError = !!Object.keys(formRef?.current?.errors || {})?.length;
  const isEmailError = !!Object.keys(emailRef?.current?.errors || {})?.length;
  const isScorecardError = !!Object.keys(scoreCardRef?.current?.errors || {})
    ?.length;

  const tabs = [
    { title: 'Details', ref: formRef, isError: isFormError },
    { title: 'Email', ref: emailRef, isError: isEmailError },
    { title: 'Scorecard', ref: scoreCardRef, isError: isScorecardError },
  ];

  const {
    name,
    kind,
    duration,
    location,
    ats_office,
    interviewers: appointedInterviewers,
    candidate_comm_template: i_candidate_comm_template,
    reviewer_comm_template: i_reviewer_comm_template,
    note_taker_enabled,
    note_taker_mandatory,
    substituted_name,
  } = interviewDetails || {};

  const {
    name: savedName,
    substituted_name: savedSubstitutedName,
    kind: savedKind,
    duration: savedDuration,
    location: savedLocation,
    ats_office: saveAtsOffice,
    participants,
    candidate_comm_template: c_candidate_comm_template,
    reviewer_comm_template: c_reviewer_comm_template,
    scorecard_config,
    note_taker_enabled: c_note_taker_enabled,
    note_taker_mandatory: c_note_taker_mandatory,
    schedule_type: c_scheduled_type,
  } = createdInterviewDetails || {};
  const { duration: formDuration, participant_ids } =
    formRef?.current?.values || {};

  const candidate_comm_template =
    i_candidate_comm_template || c_candidate_comm_template;
  const reviewer_comm_template =
    i_reviewer_comm_template || c_reviewer_comm_template;

  const errorStyles = {
    color: 'transparent',
    borderRadius: '50%',
    w: '12px',
    h: '12px',
    bgColor: 'red',
  };

  useEffect(() => {
    if (!loadTabsContent && !isLoading) setLoadTabsContent(true);
  }, [isLoading, loadTabsContent]);

  useEffect(() => {
    const { date, starts, ends, endDate } = formRef?.current?.values || {};

    onEventChange({
      activeDate: date,
      activeStart:
        !isAdhoc &&
        isToday(date) &&
        !isGreater(starts, getNext15MinuteMark(selectedTimezone))
          ? getNext15MinuteMark(selectedTimezone)
          : starts,
      activeEnd: ends,
      activeEndDate: endDate,
    });
    setEnableEndDate(!isSameDay(date, endDate));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDate, activeEnd, activeStart, activeEndDate]);

  useEffect(() => {
    const { date } = formRef?.current?.values || {};
    if (!enableEndDate) {
      formRef.current.setValues({
        ...formRef.current.values,
        endDate: date,
      });
      setActiveEndDate(date);
      setEnableEndDate(false);
    }
    if (duration && !activeStart) {
      handleStartTimeChange(
        isToday(date) ? getNext15MinuteMark(selectedTimezone) : '09:00 AM'
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDate]);

  useEffect(() => {
    const disableNoteTaker =
      feDisableAiNotetaker && !note_taker_mandatory
        ? false
        : (note_taker_enabled ?? c_note_taker_enabled ?? enableNoteTaker);
    formRef.current.setValues({
      ...formRef.current.values,
      interviewName:
        savedSubstitutedName || substituted_name || savedName || name,
      duration: savedDuration || duration || 15,
      interview_type: savedKind || kind || 'one_on_one',
      location: savedLocation || location,
      office: (saveAtsOffice || ats_office)?.id,
      enable_note_taker: disableNoteTaker,
      note_taker_mandatory: note_taker_mandatory ?? c_note_taker_mandatory,
      schedule_type: c_scheduled_type
        ? c_scheduled_type === 'Custom'
          ? 'Custom'
          : 'Selected Option'
        : 'Custom',
    });
    setDateRange(
      c_scheduled_type
        ? c_scheduled_type === 'Custom'
          ? 'Custom'
          : 'Selected Option'
        : 'Custom'
    );
    setIsNoSlotsAvailable(false);
    setInterviewType(savedKind || kind || 'one_on_one');
  }, [
    kind,
    location,
    name,
    duration,
    savedName,
    savedDuration,
    savedKind,
    savedLocation,
    saveAtsOffice,
    ats_office,
    note_taker_enabled,
    c_note_taker_enabled,
    note_taker_mandatory,
    c_note_taker_mandatory,
    c_scheduled_type,
    savedSubstitutedName,
    substituted_name,
    enableNoteTaker,
    feDisableAiNotetaker,
  ]);

  useEffect(() => {
    if (changedDateRange?.type) {
      formRef.current.setValues({
        ...formRef.current.values,
        schedule_type: changedDateRange?.type,
      });
      setDateRange(changedDateRange?.type);
      setIsNoSlotsAvailable(false);
      if (changedDateRange?.type === 'Custom') setIsCustomType(true);
    }
  }, [changedDateRange]);

  useEffect(() => {
    const { activeDate, activeStart, activeEnd, activeEndDate } = event;
    if (activeDate && activeStart && activeEnd) {
      formRef.current.setValues({
        ...formRef.current.values,
        date: activeDate,
        starts: activeStart.value,
        ends: activeEnd.value,
        endDate: activeEndDate,
      });
      setActiveStartIndex(
        timeIntervals?.findIndex((time) => {
          return time.value === activeStart?.value;
        })
      );
    }
    setEnableEndDate(!isSameDay(activeDate, activeEndDate));
  }, [event]);

  useEffect(() => {
    const { activeDate, activeStart, activeEnd, activeEndDate } = event;

    if (
      !isAdhoc &&
      executeInterviewProcess &&
      variant === 'manual' &&
      // (interviewType === 'one_on_one' && ) &&
      !(interviewId || scheduleId) &&
      activeDate &&
      activeStart &&
      activeEnd &&
      activeEndDate
    ) {
      dispatch(
        fetchSuggestedInterviewerId({
          ats_job_stage_action_id: actionId,
          interview_start: combineTimeAndDate(
            activeStart?.value,
            activeDate,
            selectedTimezone
          ),
          interview_end: combineTimeAndDate(
            activeEnd?.value,
            activeEndDate,
            selectedTimezone
          ),
        })
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    event,
    activeDate,
    activeEnd,
    activeStart,
    activeEndDate,
    actionId,
    dispatch,
  ]);

  useEffect(() => {
    return () => {
      dispatch(resetInterviewerId());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const debouncedUpdateInterviewName = debounce((name) => {
    updateInterviewName(name);
  }, 250);

  const handleInterviewNameChange = (event) => {
    const interviewName = event?.target?.value || '';
    debouncedUpdateInterviewName(interviewName);
  };

  const getSuggestedInterviewer = async () => {
    if (selectedInterviewerId) {
      const res = await fetchSuggestedInterviewerDetails({
        id: selectedInterviewerId?.fetch_uuid,
      });
      if (res?.data?.status !== 'SUCCESS') {
        throw res?.errors;
      }
      return res?.data?.results;
    }
  };

  const {
    data,
    inProgress: isInterviewerLoading,
    // error,
  } = useRetry({
    fn: getSuggestedInterviewer,
    args: {},
    retryOptions: { factor: 1, retries: 20, minTimeout: 1000 },
    start: selectedInterviewerId,
  });

  const getZoomAvailability = async () => {
    const res = await fetchZoomLimit();
    if (res?.data?.fetch_status === 'PENDING') {
      throw res?.errors;
    }
    if (res?.data?.fetch_status === 'FAILED') {
      return null;
    }
    return res?.data;
  };

  const { data: zoomAvailability } = useRetry({
    fn: getZoomAvailability,
    args: {},
    retryOptions: { factor: 1, retries: 20, minTimeout: 1000 },
    start: true,
  });

  useEffect(() => {
    const { location } = formRef.current?.values ?? {};
    if (
      location === 'zoom' &&
      (zoomAvailability?.remaining_limit === 0 ||
        zoomAvailability?.available === false)
    ) {
      formRef.current.setFieldValue('location', '');
    }
  }, [formRef?.current?.values, zoomAvailability]);

  const mapParticipants = (participants) => {
    return participants.map((participant) => ({
      ...participant,
      value: participant.id,
      label: participant.name,
    }));
  };

  const executeInterviewProcess = useMemo(() => {
    return (appointedInterviewers?.length || 0) > 1;
  }, [appointedInterviewers]);

  const participantsOptions = useMemo(() => {
    if (participants) {
      return mapParticipants(participants);
    }

    if (appointedInterviewers) {
      if (
        appointedInterviewers.length > 1 &&
        (savedKind === 'one_on_one' || kind === 'one_on_one')
      ) {
        return null;
      }
      return mapParticipants(appointedInterviewers);
    }

    return null;
  }, [participants, appointedInterviewers, savedKind, kind]);

  const suggestedInterviewer = useMemo(() => {
    if (!data?.suggested_interviewer) return null;
    return {
      ...data?.suggested_interviewer,
      value: data?.suggested_interviewer?.id,
      label: data?.suggested_interviewer?.name,
      isAiRecommendation: true,
    };
  }, [data?.suggested_interviewer]);

  const initialValues = {
    interviewName: '',
    duration: '',
    date: '',
    starts: '',
    ends: '',
    endDate: '',
    interview_type: '',
    participant_ids: participantsOptions || [],
    location: '',
    schedule_type: 'Custom',
    enable_note_taker: false,
    note_taker_mandatory: false,
  };

  const handleStartTimeChange = (value) => {
    setActiveStart(value);
    setActiveStartIndex(
      timeIntervals?.findIndex((time) => {
        return time.value === value;
      })
    );
    const { date, duration } = formRef?.current?.values || {};
    if (duration) {
      const { time: automatedEndTime, date: automatedEndDate } =
        addMinuteWithDuration(value, duration, date);

      if (date) {
        setActiveEnd(automatedEndTime);
        setActiveEndDate(automatedEndDate);
        formRef.current.setValues({
          ...formRef.current.values,
          starts: value,
          ends: automatedEndTime,
          endDate: automatedEndDate,
        });
      } else {
        formRef.current.setValues({
          ...formRef.current.values,
          starts: value,
          ends: '',
        });
      }
    }
  };

  const handleEndTimeChange = (value) => {
    setActiveEnd(value);
    if (!enableEndDate) {
      const { starts } = formRef?.current?.values || {};
      const formattedValue = moment(value, 'hh:mm A');
      const formattedStarts = moment(starts, 'hh:mm A');
      const isGreater = formattedValue.isBefore(formattedStarts);
      if (isGreater) {
        formRef.current.setValues({
          ...formRef.current.values,
          starts: value,
          ends: starts,
        });
      }
    }
  };

  const handleInterviewEvents = (value) => {
    const ids = value?.map((data) => data.id);
    selectedInterviewers(ids);
  };

  const switchToCustomDateRange = () => {
    if (dateRange === 'Selected Option') {
      changeDateRange({ type: 'Custom' });
      setDateRange('Custom');
      formRef.current.setFieldValue('schedule_type', 'Custom');
    }
  };

  const handleDurationChange = ({ value }) => {
    dispatch(updateDuration(value));
    switchToCustomDateRange();
  };

  const getAvailableSlots = async () => {
    if (scheduleSlotsData?.fetch_uuid) {
      const res = await fetchScheduleSlotsData({
        timezone: selectedTimezone,
        schedule_type: dateRange,
        user_ids: participant_ids.map((participant) => participant.id),
        slot_duration: formDuration,
        ats_job_id: jobId,
        interview_kind: interviewType,
        fetch_uuid: scheduleSlotsData?.fetch_uuid,
      });
      if (res?.data?.status !== 'SUCCESS') {
        throw res?.errors;
      }
      if (!res?.data?.slots) {
        setIsNoSlotsAvailable(true);
      }
      return res?.data?.slots || {};
    }
  };

  const {
    data: scheduleSlots,
    inProgress: isScheduleSlotLoading,
    // error,
  } = useRetry({
    fn: getAvailableSlots,
    args: {},
    retryOptions: { factor: 1, retries: 20, minTimeout: 1000 },
    start: scheduleSlotsData?.fetch_uuid,
  });

  useEffect(() => {
    handleFetchedScheduleSlots({ scheduleSlots, isScheduleSlotLoading });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleSlots, isScheduleSlotLoading]);

  useEffect(() => {
    if (
      stateDuration &&
      interviewType &&
      jobId &&
      participant_ids.length &&
      selectedTimezone &&
      dateRange !== 'Custom' &&
      dateRange !== 'Selected Option'
    ) {
      setIsNoSlotsAvailable(false);
      fetchScheduleSlots({
        timezone: selectedTimezone,
        schedule_type: dateRange,
        user_ids: participant_ids.map((participant) => participant.id),
        slot_duration: stateDuration,
        ats_job_id: jobId,
        interview_kind: interviewType,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    stateDuration,
    interviewType,
    jobId,
    participant_ids,
    selectedTimezone,
    dateRange,
  ]);

  const startTimeInterval = () => {
    const { date } = formRef?.current?.values || {};

    return isToday(date)
      ? timeIntervals.filter((time) =>
          isGreater(time.value, getNext15MinuteMark(selectedTimezone))
        )
      : timeIntervals;
  };

  const configFormByVariant = () => {
    if (variant === 'manual') {
      return [
        {
          id: 'date-fields',
          type: 'wrapper',
          Wrapper: Flex,
          wrapperProps: {
            gap: '12px',
          },
          fields: [
            {
              name: 'date',
              label: 'Start date',
              type: 'custom',
              isRequired: true,
              containerStyles: { width: '100%' },
              Field: DatePickerField,
              props: {
                onChange: (date) => {
                  setActiveDate(date);
                },
                label: 'Start date',
                fieldKey: 'date',
              },
            },
            {
              name: 'starts',
              label: 'Start time',
              type: 'custom',
              isRequired: true,
              containerStyles: { width: '100%' },
              Field: TimeField,
              props: {
                label: 'Start time',
                fieldKey: 'starts',
                options: startTimeInterval(),
                // options:
                //   !enableEndDate && timeIntervals.slice(0, activeEnd?.id),
                onChange: handleStartTimeChange,
              },
            },
            {
              name: 'ends',
              label: 'End time',
              type: 'custom',
              isRequired: true,
              containerStyles: { width: '100%' },
              Field: TimeField,
              props: {
                label: 'End time',
                fieldKey: 'ends',
                // options: timeIntervals,
                options: !enableEndDate
                  ? timeIntervals.slice(activeStartIndex + 1)
                  : timeIntervals,
                onChange: handleEndTimeChange,
              },
            },
            enableEndDate && {
              name: 'endDate',
              label: 'End date',
              type: 'custom',
              isRequired: true,
              containerStyles: { width: '100%' },
              Field: DatePickerField,
              props: {
                onChange: (date) => setActiveEndDate(date),
                label: 'End date',
                fieldKey: 'endDate',
              },
            },
          ],
        },
      ];
    }
    const fields = [
      {
        name: 'duration',
        label: 'Duration',
        type: 'select',
        isRequired: true,
        options: durationIntervals,
        customOnChange: handleDurationChange,
        containerStyles: {
          width: '100%',
        },
      },
    ];
    if (tRangeOfDays) {
      fields[1] = {
        name: 'schedule_type',
        label: 'Date range',
        type: 'select',
        isRequired: true,
        options: isReschedule
          ? rangeIntervals
          : rangeIntervals.filter(
              (interval) => interval.value !== 'Selected Option'
            ),
        customOnChange: ({ value }) => {
          changeDateRange(value);
          setDateRange(value);
          setIsNoSlotsAvailable(false);
          if (value !== 'Selected Option') {
            setScheduledTypeModified(true);
          }
        },
        containerStyles: {
          width: '100%',
        },
      };
    }
    return fields;
  };

  const locationOptions = useMemo(
    () => [
      {
        label: 'Zoom',
        value: 'zoom',
        icon: Zoom,
        isAvailable: locations?.['zoom'],
        availability: zoomAvailability?.remaining_limit,
        isDisabled:
          zoomAvailability?.remaining_limit === 0 ||
          zoomAvailability?.available === false,
      },
      {
        label: 'Google',
        value: 'google_meet',
        icon: Gmeet,
        isAvailable: locations?.['google_meet'],
      },
      {
        label: 'Teams',
        value: 'teams',
        icon: Teams,
        isAvailable: locations?.['teams'],
      },
      {
        label: 'Phone Interview',
        value: 'phone_interview',
        icon: Phone,
        isAvailable: locations?.['phone_interview'],
      },
      {
        label: 'Onsite / Physical',
        value: 'onsite',
        icon: Building07,
        isAvailable: locations?.['onsite'],
      },
    ],
    [locations, zoomAvailability]
  );

  const formConfig = [
    {
      name: 'interviewName',
      label: 'Interview name',
      type: 'input',
      isRequired: true,
      placeholder: 'Add interview name here',
      customOnChange: handleInterviewNameChange,
      maxLength: 250,
    },
    ...configFormByVariant(),
    {
      name: 'interview_type',
      label: 'Interview type',
      type: 'select',
      isRequired: false,
      placeholder: 'Select Interview Type',
      customOnChange: ({ value }) => setInterviewType(value),
      options: [
        {
          label: 'One-on-one interview',
          value: 'one_on_one',
        },
        {
          label: 'Panel interview',
          value: 'panel',
        },
      ],
      containerStyles: { width: '100%' },
      menuLabelStyles: { size: 'sm-medium' },
      menuDescStyles: { size: 'sm', color: 'secondary-700' },
    },
    {
      name: 'participant_ids',
      label: 'Add interviewer',
      type: 'custom',
      Field: AddInterviewersField,
      onChange: handleInterviewEvents,
      isRequired: true,
      validation: Yup.array()
        .required('Array is required')
        .min(1, 'At least one interviewer is required'),
      props: {
        onChange: handleInterviewEvents,
        options: interviewers,
        actionId: actionId,
        startDate: weekRange?.start,
        endDate: weekRange?.end,
        isLoading: isInterviewerLoading,
        variant,
        suggestedInterviewer: suggestedInterviewer,
        userInfo: userInfo,
        IsNoSlotsAvailable: IsNoSlotsAvailable,
      },
    },
    {
      name: 'location',
      label: 'Location',
      type: 'select',
      isRequired: true,
      width: '100%',
      containerStyles: { width: '100%' },
      customFormatLabel: SelectOptionWithIcon,
      components: { Option: SelectOptionWithAvailability },
      options: locationOptions.filter((option) => option.isAvailable),
      placeholder: 'Select Location',
      showNotes: true,
      noteCondition: {
        type: 'index',
        conditions: [{ field: 'location', value: 'phone_interview' }],
      },
    },
    {
      isConditional: true,
      conditionConfig: { field: 'location', value: 'onsite' },
      name: 'office',
      label: 'Office Address',
      type: 'select',
      isRequired: true,
      options: offices,
      containerStyles: { width: '100%' },
      placeholder: 'Select office address',
      loading: isOfficeLoading,
      valueKey: 'id',
      labelKey: 'name',
      isMulti: false,
      descKey: 'location',
      components: {
        Option: SelectWithDescriptionOption,
      },
    },

    {
      isConditional: true,
      conditionConfig: {
        type: 'or',
        conditions: [
          { field: 'location', value: 'zoom' },
          { field: 'location', value: 'google_meet' },
          { field: 'location', value: 'teams' },
        ],
      },
      name: 'enable_note_taker',
      type: 'custom',
      containerStyles: { width: '100%' },
      Field: NoteTakerField,
    },
  ];

  const handleSubmit = async (_values = {}) => {
    formRef?.current.handleSubmit();
    emailRef?.current.handleSubmit();
    const { feedback_description, is_feedback_mandatory, rating_description } =
      scoreCardRef?.current?.values || {};

    const interviewDetails = configureCreateInterviewData({
      values: {
        ...formRef?.current?.values,
        ...emailRef?.current?.values,
      },
      jobId,
      stageId,
      actionId,
      candidateId: candidateId,
      applicationId: applicationId,
      timezone: selectedTimezone,
      candidateTemplate: candidate_comm_template,
      interviewerTemplate: reviewer_comm_template,
      variant,
      schedule_type_modified: isScheduledTypeModified,
      c_scheduled_type,
      schedule_slots_modified: isCustomType,
      ...(isAdhoc && {
        scorecard_feedback_description: feedback_description,
        scorecard_rating_description: rating_description,
        scorecard_config: {
          questions: scoreCardRef?.current?.values?.scoreCards?.map(
            (scoreCard) => {
              delete scoreCard.id;
              if (scoreCard?.options) {
                scoreCard.options = scoreCard?.options?.map(
                  (option) => option.optionText
                );
              }
              return scoreCard;
            }
          ),
          is_feedback_mandatory,
        },
      }),
      ...(variant !== 'manual' && { events }),
    });

    let result;
    if (variant === 'manual') {
      if (interviewId) {
        result = await updateInterview({ interviewDetails, interviewId });
      } else {
        result = await createInterview(interviewDetails);
      }
    } else {
      if (scheduleId) {
        result = await updateScheduleInterview({
          interviewDetails,
          scheduleId,
        });
      } else {
        result = await scheduleInterview(interviewDetails);
      }
    }

    if (result?.data?.id) {
      toast({
        title:
          variant === 'manual'
            ? `Interview ${interviewId ? 'updated' : 'created'} successfully`
            : scheduleId
              ? 'Meeting scheduled updated successfully'
              : 'Self-scheduling link sent successfully',
      });
      onSuccess();
      onClose();
    } else {
      const errors = result?.error;
      const errorContext = [];
      for (const key of errors) {
        if (ERRORS[key]) {
          errorContext.push(ERRORS[key]);
        }
      }
      toast({
        title: errorContext.length
          ? errorContext.join()
          : 'Something went wrong please try again',
        status: 'error',
      });
    }
  };

  const handleSteps = async () => {
    await tabs[tabIndex]?.ref?.current?.submitForm();

    if (tabs[tabIndex]?.ref?.current?.isValid) {
      if (isAdhoc ? tabIndex < 2 : tabIndex < 1) {
        setTabIndex(tabIndex + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const handleTabs = async (stepIndex) => {
    await tabs[tabIndex]?.ref?.current?.submitForm();
    if (tabs[tabIndex]?.ref?.current?.isValid) {
      setTabIndex(stepIndex);
    }
  };

  const validateAndSetErrors = useCallback(() => {
    // const { candidateEmailEditor, interviewerEmailEditor } =
    //   emailRef.current.values || {};
    // if (!candidateEmailEditor || !interviewerEmailEditor) {
    //   setEmailError(true);
    // }
  }, []);

  const renderDetailForm = () => (
    <>
      <FormBuilder
        formRef={formRef}
        config={formConfig}
        initialValues={initialValues}
        observFunc={validateAndSetErrors}
        showCustomError={false}
      />
    </>
  );
  const renderEmailForm = useCallback(() => {
    return (
      <>
        {loadTabsContent &&
          (isAdhoc ||
            interviewDetails?.name ||
            createdInterviewDetails?.id) && (
            <Email
              formRef={emailRef}
              observFunc={validateAndSetErrors}
              activity={{
                ...((isReschedule || !isAdhoc) && { id: 1 }),
                candidate_comm_template,
                reviewer_comm_template,
              }}
            />
          )}
      </>
    );
  }, [
    candidate_comm_template,
    createdInterviewDetails?.id,
    interviewDetails?.name,
    isAdhoc,
    isReschedule,
    loadTabsContent,
    reviewer_comm_template,
    validateAndSetErrors,
  ]);

  const renderScoreCard = () => {
    return (
      <Flex w="100%">
        {loadTabsContent && (
          <ScoreCard
            formRef={scoreCardRef}
            questions={scorecard_config?.questions || []}
            observFunc={validateAndSetErrors}
            defaultScorecardParams={defaultScorecardParams}
            scorecardData={createdInterviewDetails}
          />
        )}
      </Flex>
    );
  };

  const handleBackStep = () => {
    if (tabIndex > 0) {
      setTabIndex(tabIndex - 1);
    }
  };

  return (
    <Flex
      id="schedule-form"
      w="full"
      flexDir="column"
      justifyContent="space-between"
      height="100%"
    >
      {variant !== 'manual' && isClosed && (
        <Flex
          borderRadius="8px"
          bg="#FFF2CC"
          flexDir="column"
          m="12px"
          mb="0px"
          p="10px "
          position="relative"
          h="90px"
        >
          <Box
            w="0px"
            h="0px"
            borderTop="10px solid transparent"
            borderBottom="10px solid transparent"
            borderLeft="10px solid #FFF2CC"
            position="absolute"
            bottom="10px"
            right="-10px"
          />
          <Flex justifyContent="space-between" alignItems="center">
            <KText size="sm-medium" color="secondary-900">
              New to self-schedule? Here’s how it works
            </KText>
            <Icon
              as={XClose}
              w="16px"
              h="16px"
              cursor="pointer"
              onClick={() => setClosed(false)}
            />
          </Flex>
          <KText size="sm-regular" color="secondary-700">
            You can select multiple meeting slots by choosing a date range or
            manually picking slots in the calendar to send to the candidate
          </KText>
        </Flex>
      )}
      <Tabs
        display="flex"
        flexDir="column"
        h="100%"
        maxH={`calc(100% - ${
          variant !== 'manual' && isClosed ? '150px' : '60px'
        })`}
        alignItems="stretch"
        index={tabIndex}
      >
        <TabList
          p="15px 10px 0px 10px"
          borderBottom="1px solid #D6D6D6"
          h="fit-content"
        >
          <CustomTabWithStep
            label="Details"
            step={isFormError && 'error'}
            stepStyles={isFormError && errorStyles}
            stepAfter
            arrow={!isReschedule}
            onClick={() => handleTabs(0)}
          />
          <CustomTabWithStep
            label="Email"
            step={isEmailError && 'error'}
            stepStyles={isEmailError && errorStyles}
            stepAfter
            arrow={isAdhoc && !isReschedule}
            onClick={() => handleTabs(1)}
          />
          {isAdhoc && (
            <CustomTabWithStep
              label="Scorecard"
              step={isScorecardError && 'error'}
              stepStyles={isScorecardError && errorStyles}
              stepAfter
              onClick={() => handleTabs(2)}
            />
          )}
        </TabList>
        <TabPanels w="100%" h="calc(100% - 60px)" flex={1}>
          <TabPanel h="100%" overflowX="hidden" overflowY="auto">
            {renderDetailForm()}
          </TabPanel>
          <TabPanel h="100%" flex="1" overflowX="hidden" overflowY="auto" p="0">
            {renderEmailForm()}
          </TabPanel>
          {isAdhoc && (
            <TabPanel
              maxH="100%"
              flex="1"
              overflowX="hidden"
              overflowY="auto"
              p="0"
            >
              {renderScoreCard()}
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        gap="6px"
        p="20px"
        h="60px"
      >
        <Button
          variant="secondary-lg"
          visibility={tabIndex === 0 ? 'hidden' : 'visible'}
          onClick={handleBackStep}
        >
          Back
        </Button>
        <Flex justifyContent="flex-end" gap="6px">
          <Button variant="secondary-lg" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary-lg"
            isLoading={
              isCreateInterviewLoading ||
              isUpdateInterviewLoading ||
              isSchedulingInterviewLoading ||
              isUpdateSchedulingInterviewLoading
            }
            onClick={handleSteps}
          >
            {(isAdhoc ? tabIndex < 2 : tabIndex < 1)
              ? `Next: ${tabs[tabIndex + 1]?.title}`
              : isReschedule
                ? variant === 'manual'
                  ? 'Save'
                  : 'Reschedule'
                : 'Schedule'}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
}

export default memo(SchedulingForm);
